import { create } from 'zustand'
import { pb, messageOf, type UserRecord } from '../api'

interface AuthStore {
  user: UserRecord | null
  /** True until the stored token has been read and, if present, checked. */
  loading: boolean

  signIn: (email: string, password: string) => Promise<string | null>
  /**
   * Returns null when the account is made AND signed in, `'confirm_email'` when
   * it is made but cannot authenticate yet, or an error string. `inviteCode` is
   * required: registration is closed except through /api/tomf/register.
   */
  signUp: (
    email: string,
    password: string,
    username: string,
    inviteCode: string,
  ) => Promise<'confirm_email' | string | null>
  /** Send the reset mail. The link lands on `/reset/:token`. */
  resetPassword: (email: string) => Promise<string | null>
  /** Spend a reset token from that link. */
  confirmPasswordReset: (token: string, password: string) => Promise<string | null>
  /** Change it while signed in, which needs the old one. */
  changePassword: (oldPassword: string, password: string) => Promise<string | null>
  updateUsername: (username: string) => Promise<string | null>
  /** Ask for the change. Nothing moves until the mail to the NEW address is opened. */
  updateEmail: (email: string) => Promise<string | null>
  /** Spend an email-change token, which needs the password to prove it is still them. */
  confirmEmailChange: (token: string, password: string) => Promise<string | null>
  /** Spend a verification token from `/verify/:token`. */
  confirmVerification: (token: string) => Promise<string | null>
  deleteAccount: () => Promise<string | null>
  signOut: () => Promise<void>
  initialize: () => () => void
}

const users = () => pb.collection('users')

/** Every method reports the same way: null for success, a readable string otherwise. */
async function attempt(work: () => Promise<unknown>): Promise<string | null> {
  try {
    await work()
    return null
  } catch (error) {
    return messageOf(error)
  }
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  loading: true,

  signIn: (email, password) => attempt(() => users().authWithPassword(email, password)),

  signUp: async (email, password, username, inviteCode) => {
    // The route, not users().create(): it is what validates and spends the
    // invite code, creates the account and mints its own five new keys, all as
    // one transaction (pocketbase/pb_hooks/register.pb.js). A rejected invite
    // code arrives here as a thrown ClientResponseError, same as any other
    // registration refusal (address taken, password too short, ...).
    try {
      await pb.send('/api/tomf/register', { method: 'POST', body: { email, password, username, inviteCode } })
    } catch (error) {
      return messageOf(error)
    }

    // Best effort, and not awaited into the result: the account exists either
    // way, and a mail server that is down must not read as a failed sign-up.
    // This is also what makes the repointed verification template get used.
    void users()
      .requestVerification(email)
      .catch(() => {})

    // Signing in straight away, because `users.authRule` is empty and permits
    // it. If that rule is ever tightened to `verified = true`, this is the call
    // that starts failing, and the account is real but unusable until the mail
    // is opened. Reported as `confirm_email` rather than as an error, which is
    // what the dialog already knows how to show.
    const signedIn = await attempt(() => users().authWithPassword(email, password))
    return signedIn === null ? null : 'confirm_email'
  },

  resetPassword: (email) => attempt(() => users().requestPasswordReset(email)),

  confirmPasswordReset: (token, password) =>
    attempt(() => users().confirmPasswordReset(token, password, password)),

  // PocketBase requires the old password, which Supabase did not: its session
  // was proof enough. The extra field is the honest version, since a borrowed
  // tab could otherwise change the password and keep the account.
  changePassword: async (oldPassword, password) => {
    const id = get().user?.id
    if (!id) return 'Not signed in'
    const failed = await attempt(() =>
      users().update(id, { oldPassword, password, passwordConfirm: password }),
    )
    if (failed !== null) return failed
    // A password change invalidates the token that made it, so the session has
    // to be re-established or the next request is a silent 401.
    return attempt(() => users().authWithPassword(get().user?.email ?? '', password))
  },

  updateUsername: async (username) => {
    const id = get().user?.id
    if (!id) return 'Not signed in'
    const failed = await attempt(() => users().update(id, { name: username }))
    if (failed === null) return null
    // `idx_users_name` is a unique index, so a clash arrives as a field error
    // on `name`. `messageOf` digs it out, since the top-level message is only
    // ever "Failed to update record." PocketBase's own wording for it is "Value
    // must be unique.", which is true and says nothing about what to do, so it
    // is translated here exactly as the Postgres `23505` code used to be.
    if (/unique/i.test(failed)) return 'Username is already taken'
    return failed
  },

  updateEmail: async (email) => {
    const id = get().user?.id
    if (!id) return 'Not signed in'
    // Nothing changes on the record here: PocketBase mails the NEW address and
    // waits for `/email/:token`. So the settings page says "check your inbox"
    // rather than showing the new address as though it had taken.
    return attempt(() => users().requestEmailChange(email))
  },

  confirmEmailChange: (token, password) =>
    attempt(() => users().confirmEmailChange(token, password)),

  confirmVerification: (token) => attempt(() => users().confirmVerification(token)),

  deleteAccount: async () => {
    const id = get().user?.id
    if (!id) return 'Not signed in'
    // A rule, not an RPC: `users.deleteRule` is `id = @request.auth.id`.
    // Deleting from `auth.users` needed SECURITY DEFINER in Postgres because
    // that table was not ours. Everything hanging off it cascades.
    const failed = await attempt(() => users().delete(id))
    if (failed !== null) return failed
    pb.authStore.clear()
    return null
  },

  signOut: async () => {
    pb.authStore.clear()
  },

  initialize: () => {
    const sync = () =>
      set({ user: (pb.authStore.record as UserRecord | null) ?? null, loading: false })

    if (pb.authStore.isValid) {
      // A stored token is not a session. Refreshing is what separates "signed
      // in" from "was signed in a fortnight ago": without it an expired token
      // renders the whole app as though it had a user, and every request 401s
      // with nothing on screen to explain it.
      void users()
        .authRefresh()
        .catch(() => pb.authStore.clear())
        .finally(sync)
    } else {
      // An expired token is not valid, so it never reaches the refresh above, but
      // its record stays in storage: adopting that shows a signed-in user whose
      // requests all go out as a guest. Clear it so the app reads as signed out.
      if (pb.authStore.record) pb.authStore.clear()
      sync()
    }

    // Covers sign-in, sign-out, a refresh landing, and another tab doing any of
    // them: the SDK persists to localStorage and watches it.
    return pb.authStore.onChange(sync)
  },
}))
